import { AlertCircle, CheckCircle2, Clock, XCircle } from "lucide-react";

import { Badge } from "@workspace/ui/components/badge";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";
import { TimeAgo } from "@workspace/ui/components/time-ago";

interface HealthEntry {
	data: Record<string, unknown>;
	description?: string;
	duration: string;
	status: "Healthy" | "Unhealthy" | "Degraded";
	tags: string[];
}

export interface HealthData {
	status: "Healthy" | "Unhealthy" | "Degraded";
	totalDuration: string;
	entries: Record<string, HealthEntry>;
	lastCalledAt?: string;
}

interface HealthStatusProps {
	data: HealthData;
}

function getStatusIcon(status: string) {
	switch (status) {
		case "Healthy":
			return <CheckCircle2 className="size-5 text-green-600" />;
		case "Unhealthy":
			return <XCircle className="size-5 text-red-600" />;
		case "Degraded":
			return <AlertCircle className="size-5 text-yellow-600" />;
		default:
			return null;
	}
}

function getStatusVariant(
	status: string,
): "default" | "destructive" | "secondary" {
	switch (status) {
		case "Healthy":
			return "default";
		case "Unhealthy":
			return "destructive";
		case "Degraded":
			return "secondary";
		default:
			return "secondary";
	}
}

export function HealthStatus({ data }: HealthStatusProps) {
	return (
		<div className="w-full space-y-6 p-4">
			{/* Overall Status Card */}
			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							{getStatusIcon(data.status)}
							<CardTitle className="text-2xl">System Health</CardTitle>
						</div>
						<Badge variant={getStatusVariant(data.status)} className="text-sm">
							{data.status}
						</Badge>
					</div>
					<CardDescription className="mt-2 space-y-2">
						<div className="flex items-center gap-2">
							<Clock className="size-4" />
							<span>Total Duration: {data.totalDuration}</span>
						</div>
						{data.lastCalledAt && (
							<div className="text-xs text-muted-foreground">
								Last called: <TimeAgo date={data.lastCalledAt} />
							</div>
						)}
					</CardDescription>
				</CardHeader>
			</Card>

			{/* Individual Service Cards */}
			<div className="grid gap-4 md:grid-cols-2">
				{Object.entries(data.entries).map(([serviceName, entry]) => (
					<Card key={serviceName}>
						<CardHeader>
							<div className="flex items-center justify-between">
								<CardTitle className="font-mono text-lg">
									{serviceName}
								</CardTitle>
								<Badge variant={getStatusVariant(entry.status)}>
									{entry.status}
								</Badge>
							</div>
							{entry.description && (
								<CardDescription>{entry.description}</CardDescription>
							)}
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="flex items-center gap-2 text-sm text-muted-foreground">
								<Clock className="size-4" />
								<span>{entry.duration}</span>
							</div>

							{entry.tags.length > 0 && (
								<div className="flex flex-wrap gap-2">
									{entry.tags.map((tag) => (
										<Badge key={tag} variant="outline" className="text-xs">
											{tag}
										</Badge>
									))}
								</div>
							)}
						</CardContent>
					</Card>
				))}
			</div>
		</div>
	);
}
